<template>
	<div v-if="isOpen" class="modal-overlay" @click="closeModal">
		<form class="modal-content" @click.stop @submit.prevent="onSave">
			<div class="modal-header">
				<div>
					<h3>{{ label("title") }}</h3>
					<p>{{ label("subtitle") }}</p>
				</div>
				<button type="button" class="close-btn" :disabled="saving" @click="closeModal">&times;</button>
			</div>

			<div class="modal-body">
				<div v-if="submitError" class="modal-error">{{ submitError }}</div>

				<section class="section">
					<h4>{{ label("setup") }}</h4>
					<div class="grid two">
						<label class="field">
							<span class="field-label">{{ label("projectId") }}</span>
							<input
								v-model.trim="projectId"
								type="text"
								class="field-input"
								:placeholder="label('projectIdPlaceholder')"
								:disabled="saving"
							/>
						</label>

						<div class="field">
							<span class="field-label">{{ label("count") }}</span>
							<div class="count-stepper">
								<button
									type="button"
									class="stepper-button"
									:aria-label="label('decreaseCount')"
									:disabled="saving || isMinOpportunityCount"
									@click="decreaseOpportunityCount"
								>
									-
								</button>
								<input
									v-model.number="opportunityCount"
									type="number"
									class="field-input count-input"
									:min="minOpportunityCount"
									:max="maxOpportunityCount"
									:disabled="saving"
									@blur="normalizeOpportunityCount"
								/>
								<button
									type="button"
									class="stepper-button"
									:aria-label="label('increaseCount')"
									:disabled="saving || isMaxOpportunityCount"
									@click="increaseOpportunityCount"
								>
									+
								</button>
							</div>
						</div>

						<label class="field full">
							<span class="field-label">{{ label("parcelIds") }}</span>
							<textarea
								v-model="parcelIdsText"
								class="field-input textarea"
								:placeholder="label('parcelIdsPlaceholder')"
								:disabled="saving"
							/>
							<span v-if="parcelIdsValidation" class="field-error">{{ parcelIdsValidation }}</span>
						</label>
					</div>
				</section>

				<section class="section">
					<h4>{{ label("opportunityFilters") }}</h4>
					<div class="grid two">
						<div class="field">
							<span class="field-label">{{ $t("filters.dev-type-title") }}</span>
							<ArkListboxDropdown
								v-model="opportunityType"
								:options="opportunityTypeOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('opportunities.all-dev-types')"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<span class="field-label">{{ $t("filters.priority") }}</span>
							<ArkListboxDropdown
								v-model="priority"
								:options="priorityOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.priority')"
								:same-width="true"
							/>
						</div>
					</div>
				</section>

				<section class="section">
					<h4>{{ $t("filters.classification-title") }}</h4>
					<div class="grid three">
						<div class="field">
							<span class="field-label">{{ $t("filters.asset-type") }}</span>
							<ArkListboxDropdown
								v-model="assetTypes"
								:options="assetTypeOptions"
								:multiple="true"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.asset-type')"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<span class="field-label">{{ $t("filters.land-category") }}</span>
							<ArkListboxDropdown
								v-model="landCategories"
								:options="landCategoryOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving || shouldDisableLandCategory"
								:placeholder="$t('filters.land-category')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<span class="field-label">{{ $t("filters.land-subcategory") }}</span>
							<ArkListboxDropdown
								v-model="landSubcategories"
								:options="landSubcategoryOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving || shouldDisableSubcategory"
								:placeholder="$t('filters.land-subcategory')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>
					</div>
				</section>

				<section class="section">
					<h4>{{ $t("filters.transaction") }}</h4>
					<div class="grid five">
						<div class="field">
							<ArkListboxDropdown
								v-model="transactionStatuses"
								:options="transactionStatusOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving"
								:placeholder="$t('filters.transaction-status-title')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<ArkListboxDropdown
								v-model="transactionVerificationStatus"
								:options="transactionVerificationOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.transaction-verification-status-title')"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<ArkListboxDropdown
								v-model="transactionBuyerStatus"
								:options="buyerStatusOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.buyer-status-title')"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<ArkListboxDropdown
								v-model="transactionSource"
								:options="transactionSourceOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.transaction-source-title')"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<ArkListboxDropdown
								v-model="transactionComposition"
								:options="transactionCompositionOptions"
								:multiple="false"
								:clearable="true"
								:searchable="false"
								:disabled="saving"
								:placeholder="$t('filters.transaction-composition-title')"
								:same-width="true"
							/>
						</div>
					</div>
				</section>

				<section class="section">
					<h4>{{ $t("filters.others-title") }}</h4>
					<div class="range-grid">
						<div class="range-field">
							<span class="field-label">{{ landAreaLabel }}</span>
							<input
								v-model="sizeValue"
								type="text"
								inputmode="numeric"
								class="field-input"
								:disabled="saving"
								@blur="sizeValue = normalizeNumericText(sizeValue, areaRangeMax)"
							/>
						</div>

						<div class="range-field">
							<span class="field-label">{{ buildableAreaLabel }}</span>
							<input
								v-model="buildableAreaValue"
								type="text"
								inputmode="numeric"
								class="field-input"
								:disabled="saving"
								@blur="buildableAreaValue = normalizeNumericText(buildableAreaValue, areaRangeMax)"
							/>
						</div>

						<div class="range-field">
							<span class="field-label">{{ $t("opportunities.price") }}</span>
							<input
								v-model="priceValue"
								type="text"
								inputmode="numeric"
								class="field-input"
								:disabled="saving"
								@blur="priceValue = normalizeNumericText(priceValue, priceRangeMax)"
							/>
						</div>

						<div class="range-field">
							<span class="field-label">{{ $t("opportunities.residentialUnits") }}</span>
							<input
								v-model="residentialUnitsValue"
								type="text"
								inputmode="numeric"
								class="field-input"
								:disabled="saving"
								@blur="residentialUnitsValue = normalizeNumericText(residentialUnitsValue, residentialUnitsMax)"
							/>
						</div>
					</div>
				</section>

				<section class="section">
					<h4>{{ $t("opportunities.locationAndType") }}</h4>
					<div class="grid three">
						<div class="field">
							<span class="field-label">{{ $t("filters.regions-title") }}</span>
							<ArkListboxDropdown
								v-model="regions"
								:options="regionOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving || loadingLocations"
								:placeholder="$t('opportunities.all-regions')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<span class="field-label">{{ $t("filters.municipalities-title") }}</span>
							<ArkListboxDropdown
								v-model="municipalities"
								:options="municipalityOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving || loadingLocations || shouldDisableMunicipality"
								:placeholder="$t('opportunities.all-cities')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>

						<div class="field">
							<span class="field-label">{{ $t("filters.boroughs-title") }}</span>
							<ArkListboxDropdown
								v-model="boroughs"
								:options="boroughOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:disabled="saving || loadingLocations || shouldDisableBorough"
								:placeholder="$t('opportunities.all-boroughs')"
								:search-normalizer="normalizeLabel"
								:same-width="true"
							/>
						</div>
					</div>
				</section>
			</div>

			<div class="modal-footer">
				<div v-if="createdDealIds.length > 0" class="created-deals">
					<span class="created-label">{{ label("createdDeals") }}</span>
					<a
						v-for="dealId in createdDealIds"
						:key="dealId"
						class="deal-button"
						:href="hubspotDealUrl(dealId)"
						target="_blank"
						rel="noopener noreferrer"
						:title="dealId"
					>
						{{ label("dealButton") }} {{ dealId }}
					</a>
				</div>
				<div class="footer-actions">
					<Button variant="secondary" size="m" type="button" :disabled="saving" @click="closeModal">
						{{ label("cancel") }}
					</Button>
					<Button variant="primary" size="m" type="submit" :disabled="saving || !canSave">
						{{ saving ? label("saving") : label("save") }}
					</Button>
				</div>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import Button from "@/components/ui/Button.vue"
import { UseRootStore } from "@/model/RootStore"
import { RANGE_MAX, TransactionVerificationStatus } from "@/types"
import {
	TransactionComposition,
	TransactionMetadataBuyerStatus,
	TransactionMetadataStatus,
	TransactionSource,
	type BoroughRecord,
	type Municipality,
} from "@/model/DataModel"
import { AssetType, LandCategory, LandSubcategory, OpportunityPriority, OpportunityType } from "@/graphql"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { transactionMetadataStatusTranslationKeys } from "@/utils/filterTranslationKeys"
import { AreaUnit, convertAreaRange } from "@/utils/unitSystem"

type OptionValue = string | number | null
type Option = {
	value: OptionValue
	label: string
}

type RangePayload = {
	min: number
	max: number
}

interface Props {
	isOpen: boolean
	saving?: boolean
	submitError?: string
	createdDealIds?: string[]
	translationKey?: string
}

interface Emits {
	(e: "close"): void
	(
		e: "save",
		payload: {
			projectId: string | null
			count: number
			parcelIds: string[]
			opportunityType: OpportunityType
			priority: OpportunityPriority | null
			assetTypes: AssetType[]
			landCategories: LandCategory[]
			landSubcategories: LandSubcategory[]
			transactionStatuses: TransactionMetadataStatus[]
			transactionVerificationStatus: TransactionVerificationStatus | null
			transactionBuyerStatus: TransactionMetadataBuyerStatus | null
			transactionSource: TransactionSource | null
			transactionComposition: TransactionComposition | null
			sizeSqft: RangePayload
			buildableSqft: RangePayload
			price: RangePayload
			residentialUnits: RangePayload
			regions: string[]
			municipalities: string[]
			boroughs: string[]
		}
	): void
}

const props = withDefaults(defineProps<Props>(), {
	saving: false,
	submitError: "",
	createdDealIds: () => [],
	translationKey: "adminPage.createOpportunitiesModal",
})

const emit = defineEmits<Emits>()
const { t } = useI18n()
const { areaUnit, areaUnitSymbol, convertAreaToPreferredUnit } = useUnitSystem()
const store = UseRootStore()

const areaRangeMaxSquareFeet = RANGE_MAX
const priceRangeMax = RANGE_MAX
const residentialUnitsMax = 2_000
const minOpportunityCount = 1
const maxOpportunityCount = 100
const hubspotDealEndpoint = ((import.meta.env.VITE_HUBSPOT_DEAL_ENDPOINT as string | undefined) ?? "").replace(
	/\/$/,
	""
)

const projectId = ref("")
const opportunityCount = ref<number>(1)
const parcelIdsText = ref("")
const opportunityType = ref<OpportunityType | null>(null)
const priority = ref<OpportunityPriority | "all" | null>(null)
const assetTypes = ref<AssetType[]>([])
const landCategories = ref<LandCategory[]>([])
const landSubcategories = ref<LandSubcategory[]>([])
const transactionStatuses = ref<TransactionMetadataStatus[]>([])
const transactionVerificationStatus = ref<TransactionVerificationStatus | null>(null)
const transactionBuyerStatus = ref<TransactionMetadataBuyerStatus | null>(null)
const transactionSource = ref<TransactionSource | null>(null)
const transactionComposition = ref<TransactionComposition | null>(null)
const sizeValue = ref("")
const buildableAreaValue = ref("")
const priceValue = ref("")
const residentialUnitsValue = ref("")
const regions = ref<string[]>([])
const municipalities = ref<string[]>([])
const boroughs = ref<string[]>([])

const regionNames = ref<string[]>([])
const municipalityRecords = ref<Municipality[]>([])
const boroughRecords = ref<BoroughRecord[]>([])
const loadingLocations = ref(false)

const label = (key: string) => t(`${props.translationKey}.${key}`)

const areaRangeMax = computed(() => Math.round(convertAreaToPreferredUnit(areaRangeMaxSquareFeet, AreaUnit.SquareFeet)))
const landAreaLabel = computed(() => `${t("opportunities.unitFields.superficy")} (${areaUnitSymbol.value})`)
const buildableAreaLabel = computed(() => `${t("opportunities.unitFields.constructible")} (${areaUnitSymbol.value})`)
const hasProjectId = computed(() => projectId.value.trim().length > 0)
const parsedParcelIds = computed(() => parseParcelIds(parcelIdsText.value))
const normalizedOpportunityCount = computed(() => clampOpportunityCount(opportunityCount.value))
const isMinOpportunityCount = computed(() => normalizedOpportunityCount.value <= minOpportunityCount)
const isMaxOpportunityCount = computed(() => normalizedOpportunityCount.value >= maxOpportunityCount)
const parcelIdsValidation = computed(() => {
	if (parsedParcelIds.value.length === 0) {
		return label("parcelIdsRequired")
	}

	if (parsedParcelIds.value.length < normalizedOpportunityCount.value) {
		return t(`${props.translationKey}.parcelIdsCountMismatch`, {
			count: normalizedOpportunityCount.value,
		})
	}

	return ""
})

const opportunityTypeOptions = computed<Option[]>(() => {
	const types: OpportunityType[] = [
		OpportunityType.ListingLanderz,
		OpportunityType.ListingPotential,
		OpportunityType.OffMarketOfficial,
	]

	if (hasProjectId.value) {
		types.push(OpportunityType.OpportunityTarget)
	}

	return types.map(value => ({
		value,
		label: t(`opportunities-layers.${value}`),
	}))
})

const priorityOptions = computed<Option[]>(() => [
	{ value: "all", label: t("filters.priority-all") },
	...Object.values(OpportunityPriority).map(value => ({
		value,
		label: t(`opportunities-table.priority.${value}`),
	})),
])

const assetTypeOptions = computed<Option[]>(() =>
	Object.values(AssetType).map(value => ({
		value,
		label: t(`property.assetType.${value}`),
	}))
)

const landCategoryOptions = computed<Option[]>(() =>
	Object.values(LandCategory).map(value => ({
		value,
		label: t(`property.landCategory.${value}`),
	}))
)

const landSubcategoryLabelKey: Record<LandSubcategory, string> = {
	[LandSubcategory.LowRise]: "LowRise",
	[LandSubcategory.MidRise]: "MidRise",
	[LandSubcategory.HighRise]: "HighRise",
	[LandSubcategory.TownHouse]: "TownHouse",
	[LandSubcategory.SingleFamily]: "SingleFamily",
	[LandSubcategory.LightMedium]: "LightMedium",
	[LandSubcategory.Heavy]: "Heavy",
	[LandSubcategory.FullyCultivated]: "FullyCultivated",
	[LandSubcategory.MostlyCultivated]: "MostlyCultivated",
	[LandSubcategory.HalfAndHalf]: "HalfAndHalf",
	[LandSubcategory.MostlyForestry]: "MostlyForestry",
	[LandSubcategory.FullForestry]: "FullForestry",
}

const landSubcategoryOptionsByCategory: Record<LandCategory, LandSubcategory[]> = {
	[LandCategory.Residential]: [
		LandSubcategory.LowRise,
		LandSubcategory.MidRise,
		LandSubcategory.HighRise,
		LandSubcategory.TownHouse,
		LandSubcategory.SingleFamily,
	],
	[LandCategory.Commercial]: [LandSubcategory.LowRise, LandSubcategory.MidRise, LandSubcategory.HighRise],
	[LandCategory.Industrial]: [LandSubcategory.LightMedium, LandSubcategory.Heavy],
	[LandCategory.Agricultural]: [
		LandSubcategory.FullyCultivated,
		LandSubcategory.MostlyCultivated,
		LandSubcategory.HalfAndHalf,
		LandSubcategory.MostlyForestry,
		LandSubcategory.FullForestry,
	],
	[LandCategory.Institutional]: [],
	[LandCategory.Conservation]: [],
	[LandCategory.Forestry]: [],
	[LandCategory.Recreational]: [],
	[LandCategory.Other]: [],
}

const landSubcategoryOptions = computed<Option[]>(() => {
	const values = new Set<LandSubcategory>()
	for (const category of landCategories.value as LandCategory[]) {
		for (const subcategory of landSubcategoryOptionsByCategory[category] ?? []) {
			values.add(subcategory)
		}
	}

	return [...values].map(value => ({
		value,
		label: t(`property.landSubcategory.${landSubcategoryLabelKey[value]}`),
	}))
})

const transactionStatusOptions = computed<Option[]>(() =>
	Object.values(TransactionMetadataStatus).map(value => ({
		value,
		label: t(transactionMetadataStatusTranslationKeys[value]),
	}))
)

const transactionVerificationOptions = computed<Option[]>(() => [
	{ value: TransactionVerificationStatus.Verified, label: t("filters.transaction-verification-status-verified") },
	{ value: TransactionVerificationStatus.Unverified, label: t("filters.transaction-verification-status-unverified") },
])

const buyerStatusOptions = computed<Option[]>(() =>
	Object.values(TransactionMetadataBuyerStatus).map(value => ({
		value,
		label: t(`filters.buyer-status-${value}`),
	}))
)

const transactionSourceOptions = computed<Option[]>(() =>
	Object.values(TransactionSource).map(value => ({
		value,
		label: t(`filters.transaction-source-${value}`),
	}))
)

const transactionCompositionOptions = computed<Option[]>(() =>
	Object.values(TransactionComposition).map(value => ({
		value,
		label: t(`filters.transaction-composition-${value}`),
	}))
)

const regionOptions = computed<Option[]>(() => regionNames.value.map(name => ({ value: name, label: name })))

const selectedRegionNames = computed(() => new Set(regions.value))
const selectedMunicipalityNames = computed(() => new Set(municipalities.value))

const municipalityOptions = computed<Option[]>(() =>
	municipalityRecords.value
		.filter(municipality => municipality.regionName && selectedRegionNames.value.has(municipality.regionName))
		.map(municipality => ({ value: municipality.name, label: municipality.name }))
		.sort((left, right) => left.label.localeCompare(right.label))
)

const boroughOptions = computed<Option[]>(() =>
	boroughRecords.value
		.filter(borough => borough.municipalityName && selectedMunicipalityNames.value.has(borough.municipalityName))
		.map(borough => ({ value: borough.name, label: borough.name }))
		.sort((left, right) => left.label.localeCompare(right.label))
)

const shouldDisableLandCategory = computed(
	() => assetTypes.value.length > 0 && !assetTypes.value.includes(AssetType.Land)
)
const shouldDisableSubcategory = computed(() => shouldDisableLandCategory.value || landCategories.value.length === 0)
const shouldDisableMunicipality = computed(() => regions.value.length === 0)
const shouldDisableBorough = computed(() => municipalities.value.length === 0)
const needsProjectId = computed(() => opportunityType.value === OpportunityType.OpportunityTarget)
const canSave = computed(
	() =>
		(!needsProjectId.value || hasProjectId.value) &&
		opportunityType.value !== null &&
		normalizedOpportunityCount.value > 0 &&
		!parcelIdsValidation.value
)

function normalizeLabel(name: string) {
	return name
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
}

function parseNumericText(value: string, fallback: number, maxLimit: number): number {
	const normalizedValue = value.replace(/[,\s]/g, "")
	if (!normalizedValue) return fallback

	const numericValue = Number(normalizedValue)
	if (!Number.isFinite(numericValue)) return fallback
	return Math.min(maxLimit, Math.max(0, Math.trunc(numericValue)))
}

function normalizeNumericText(value: string, maxLimit: number): string {
	const normalizedValue = value.replace(/[,\s]/g, "")
	if (!normalizedValue) return ""
	return String(parseNumericText(value, maxLimit, maxLimit))
}

function rangePayload(value: string, maxLimit: number): RangePayload {
	return {
		min: 0,
		max: parseNumericText(value, maxLimit, maxLimit),
	}
}

function squareFeetAreaRangePayload(value: string): RangePayload {
	const range = rangePayload(value, areaRangeMax.value)
	const [min, max] = convertAreaRange([range.min, range.max], areaUnit.value, AreaUnit.SquareFeet)

	return {
		min: Math.round(min),
		max: Math.min(areaRangeMaxSquareFeet, Math.round(max)),
	}
}

function hubspotDealUrl(dealId: string): string {
	return `${hubspotDealEndpoint}/${dealId}`
}

function parseParcelIds(value: string): string[] {
	return [
		...new Set(
			value
				.split(/[\s,]+/)
				.map(parcelId => parcelId.trim())
				.filter(Boolean)
		),
	]
}

function clampOpportunityCount(value: unknown): number {
	const numericValue = Number(value)
	if (!Number.isFinite(numericValue)) return minOpportunityCount
	return Math.min(maxOpportunityCount, Math.max(minOpportunityCount, Math.trunc(numericValue)))
}

function normalizeOpportunityCount() {
	opportunityCount.value = clampOpportunityCount(opportunityCount.value)
}

function decreaseOpportunityCount() {
	opportunityCount.value = clampOpportunityCount(normalizedOpportunityCount.value - 1)
}

function increaseOpportunityCount() {
	opportunityCount.value = clampOpportunityCount(normalizedOpportunityCount.value + 1)
}

function resetForm() {
	projectId.value = ""
	opportunityCount.value = 10
	parcelIdsText.value = ""
	opportunityType.value = null
	priority.value = null
	assetTypes.value = []
	landCategories.value = []
	landSubcategories.value = []
	transactionStatuses.value = []
	transactionVerificationStatus.value = null
	transactionBuyerStatus.value = null
	transactionSource.value = null
	transactionComposition.value = null
	sizeValue.value = ""
	buildableAreaValue.value = ""
	priceValue.value = ""
	residentialUnitsValue.value = ""
	regions.value = []
	municipalities.value = []
	boroughs.value = []
}

async function loadLocations() {
	loadingLocations.value = true
	try {
		const [loadedRegionNames, loadedMunicipalities, loadedBoroughs] = await Promise.all([
			store.getRegions,
			store.getMunicipalityRecords,
			store.getBoroughRecords,
		])
		regionNames.value = loadedRegionNames
		municipalityRecords.value = loadedMunicipalities
		boroughRecords.value = loadedBoroughs
	} finally {
		loadingLocations.value = false
	}
}

function filterValuesByOptions<T extends string>(values: T[], options: Option[]): T[] {
	const validValues = new Set(options.map(option => String(option.value)))
	return values.filter(value => validValues.has(value))
}

watch(
	() => props.isOpen,
	open => {
		if (!open) return
		resetForm()
		loadLocations().catch(error => {
			console.error("Failed to load location filters:", error)
		})
	}
)

watch(assetTypes, () => {
	if (shouldDisableLandCategory.value) {
		landCategories.value = []
		landSubcategories.value = []
	}
})

watch(hasProjectId, hasProject => {
	if (!hasProject && opportunityType.value === OpportunityType.OpportunityTarget) {
		opportunityType.value = null
	}
})

watch(
	[landCategories, landSubcategoryOptions],
	() => {
		if (shouldDisableSubcategory.value) {
			landSubcategories.value = []
			return
		}
		landSubcategories.value = filterValuesByOptions(landSubcategories.value, landSubcategoryOptions.value)
	},
	{ deep: true }
)

watch(
	[regions, municipalityOptions],
	() => {
		if (shouldDisableMunicipality.value) {
			municipalities.value = []
			return
		}
		municipalities.value = filterValuesByOptions(municipalities.value, municipalityOptions.value)
	},
	{ deep: true }
)

watch(
	[municipalities, boroughOptions],
	() => {
		if (shouldDisableBorough.value) {
			boroughs.value = []
			return
		}
		boroughs.value = filterValuesByOptions(boroughs.value, boroughOptions.value)
	},
	{ deep: true }
)

function closeModal() {
	if (!props.saving) {
		emit("close")
	}
}

function onSave() {
	if (!canSave.value || opportunityType.value === null) return

	const count = normalizedOpportunityCount.value
	const trimmedProjectId = projectId.value.trim()
	emit("save", {
		projectId: trimmedProjectId || null,
		count,
		parcelIds: parsedParcelIds.value.slice(0, count),
		opportunityType: opportunityType.value,
		priority: priority.value === "all" ? null : priority.value,
		assetTypes: [...assetTypes.value],
		landCategories: [...landCategories.value],
		landSubcategories: [...landSubcategories.value],
		transactionStatuses: [...transactionStatuses.value],
		transactionVerificationStatus: transactionVerificationStatus.value,
		transactionBuyerStatus: transactionBuyerStatus.value,
		transactionSource: transactionSource.value,
		transactionComposition: transactionComposition.value,
		sizeSqft: squareFeetAreaRangePayload(sizeValue.value),
		buildableSqft: squareFeetAreaRangePayload(buildableAreaValue.value),
		price: rangePayload(priceValue.value, priceRangeMax),
		residentialUnits: rangePayload(residentialUnitsValue.value, residentialUnitsMax),
		regions: [...regions.value],
		municipalities: [...municipalities.value],
		boroughs: [...boroughs.value],
	})
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 1000;
}

.modal-content {
	width: min(1080px, 96vw);
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	background: var(--primary-background, #fff);
	border-radius: 8px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	overflow: hidden;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 20px;
	padding: 20px 24px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;

	h3 {
		margin: 0;
		color: #333;
		font-size: 1.35rem;
	}

	p {
		margin: 6px 0 0;
		color: #616161;
		font-size: 14px;
		line-height: 1.35;
	}

	.close-btn {
		width: 30px;
		height: 30px;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: none;
		background: none;
		color: #666;
		font-size: 24px;
		cursor: pointer;
	}
}

.modal-body {
	display: flex;
	flex-direction: column;
	gap: 22px;
	padding: 22px 24px;
	overflow-y: auto;
}

.modal-error {
	padding: 12px 14px;
	border: 1px solid #ffcdd2;
	border-radius: 6px;
	background: #ffebee;
	color: #c62828;
	font-size: 14px;
}

.section {
	display: grid;
	gap: 14px;

	h4 {
		margin: 0;
		color: #333;
		font-size: 15px;
		font-weight: 700;
	}
}

.grid {
	display: grid;
	gap: 14px;

	&.two {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	&.three {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	&.five {
		grid-template-columns: repeat(5, minmax(0, 1fr));
	}
}

.field,
.range-field {
	display: grid;
	gap: 6px;
	min-width: 0;
}

.full {
	grid-column: 1 / -1;
}

.field-label {
	color: #455a64;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.2;
}

.field-input {
	width: 100%;
	height: 40px;
	padding: 8px 10px;
	border: 1px solid #d0d0d0;
	border-radius: 6px;
	background: #fff;
	color: #222;
	font-size: 14px;
	box-sizing: border-box;

	&:focus {
		outline: none;
		border-color: #2e7d32;
		box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.16);
	}

	&:disabled {
		background: #f7f7f7;
		color: #9e9e9e;
		cursor: not-allowed;
	}
}

.textarea {
	min-height: 86px;
	line-height: 1.4;
	resize: vertical;
}

.field-error {
	color: #c62828;
	font-size: 12px;
	line-height: 1.3;
}

.count-stepper {
	display: grid;
	grid-template-columns: 38px minmax(0, 1fr) 38px;
	align-items: center;
	gap: 8px;
}

.count-input {
	text-align: center;
}

.stepper-button {
	width: 38px;
	height: 38px;
	border: 1px solid #d0d0d0;
	border-radius: 6px;
	background: #fff;
	color: #263238;
	font-size: 20px;
	font-weight: 700;
	line-height: 1;
	cursor: pointer;

	&:hover:not(:disabled) {
		background: #f5f5f5;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.range-grid {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 18px 22px;
}

.modal-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid #e0e0e0;
	background: #f8f9fa;
}

.created-deals {
	display: flex;
	flex: 1;
	align-items: center;
	gap: 8px;
	min-width: 0;
	max-height: 84px;
	overflow-y: auto;
	flex-wrap: wrap;
}

.created-label {
	color: #455a64;
	font-size: 13px;
	font-weight: 600;
	white-space: nowrap;
}

.deal-button {
	display: inline-flex;
	align-items: center;
	min-height: 32px;
	max-width: 180px;
	padding: 6px 10px;
	border: 1px solid #1976d2;
	border-radius: 6px;
	background: #fff;
	color: #1565c0;
	font-size: 13px;
	font-weight: 600;
	line-height: 1.2;
	text-decoration: none;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	&:hover {
		background: #e3f2fd;
	}
}

.footer-actions {
	display: flex;
	gap: 12px;
	align-items: center;
}

@media (max-width: 900px) {
	.grid.two,
	.grid.three,
	.grid.five,
	.range-grid {
		grid-template-columns: 1fr;
	}

	.modal-footer {
		align-items: stretch;
		flex-direction: column;
	}

	.footer-actions {
		justify-content: flex-end;
	}
}
</style>
